import React, { Component } from "react";
import { toLuxon } from "../../dateTimeUtils";
import { DateTime } from "luxon";
import "./chart.scss";
import { observable, makeObservable } from "mobx";
import { observer } from "mobx-react";
import * as Chart from "chart.js";
import * as _ from "lodash";
import "chartjs-plugin-zoom";
import * as tinyColor from "tinycolor2";
import { stCompLabels } from "../../Chiclet";

class TrendChart extends Component {
  /**
   * props = {
   *    trend: [{
   *        startTime: timestamp (for offsets)
   *        values: [
   *          {
   *             offset: number of seconds from the startTime,
   *             value: number
   *          },
   *          ...
   *        ],
   *
   *    }],
   *    color: '#00ff00'
   * }
   **/
  constructor(props) {
    super(props);

    makeObservable(this, {
      store: observable,
    });

    this.renderChart = this.renderChart.bind(this);
  }

  store = {
    width: window.innerWidth, // This is a reasonable default, but will be overridden.
    height: 140,
  };

  componentDidMount() {
    this.store.width = this.refCanvas.clientWidth;
    requestAnimationFrame(this.renderChart);
  }

  componentDidUpdate() {
    this.store.width = this.refCanvas.clientWidth;
    requestAnimationFrame(this.renderChart);
  }

  get toolTipPadding() {
    return (this.props.trend?.length || 0) * 10;
  }
  render() {
    return (
      <canvas
        className="trend-chart"
        ref={(r) => (this.refCanvas = r)}
        width={this.store.width}
        height={this.store.height + this.toolTipPadding}
        onClick={this.canvasClick}
      />
    );
  }

  renderChart() {
    if (!this.props.trend || !this.refCanvas) return;
    const trend = this.props.trend;

    const dates = _.get(trend, "[0].values", []).map((d) =>
      toLuxon(trend[0].startTime).plus({ seconds: d.offset }).toISO(),
    );

    this.ctx = this.refCanvas.getContext("2d");
    const chartColor = this.props.color || chartColors[0];

    const lines = (trend || []).map((trendLine, i) => {
      const lineColor =
        trend.length > 3 ? stColors[i % stColors.length] : chartColor;
      return {
        type: "line",
        borderColor: lineColor,
        pointBackgroundColor: lineColor,
        fill: false,
        data: trendLine.values.map((d) => d.value),
        cubicInterpolationMode: "monotone",
      };
    });

    const bars = (trend || [])
      .filter((trendLine) => trendLine.values.find((d) => d.setting))
      .map((trendLine) => ({
        type: "bar",
        backgroundColor: tinyColor(chartColor).setAlpha(0.4),
        data: trendLine.values.map((d) => d.setting),
        hoverBackgroundColor: tinyColor(chartColor).setAlpha(0.7),
      }));

    const datasets = [...lines, ...bars];

    let chart = new Chart(this.ctx, {
      type: "line",
      data: {
        labels: dates,
        datasets,
      },
      options: {
        layout: {
          padding: {
            bottom: this.toolTipPadding,
          },
        },
        responsive: true,
        legend: {
          display: false,
        },
        animation: {
          duration: 0,
        },
        tooltips: {
          mode: "index",
          intersect: false,
          callbacks: {
            label: (tooltipItem, data) => {
              this.tooltipTimeISO = data.labels[tooltipItem.index];
              if (trend.length > 3) {
                return `${stCompLabels[tooltipItem.datasetIndex]}: ${
                  tooltipItem.yLabel
                }`;
              } else {
                return tooltipItem.yLabel;
              }
            },
          },
        },
        scales: {
          yAxes: [
            {
              afterFit: (scaleInstance) => (scaleInstance.width = 50),
            },
          ],
          xAxes: [
            {
              type: "time",
              distribution: "series",
              time: {
                tooltipFormat: "HH:mm",
                displayFormats: {
                  year: "HH:mm",
                  quarter: "HH:mm",
                  month: "HH:mm",
                  week: "HH:mm",
                  day: "HH:mm",
                  hour: "HH:mm",
                  minute: "HH:mm",
                  second: "HH:mm:ss",
                  millisecond: "HH:mm:ss.SSS",
                },
                stepSize: 2,
              },
              barThickness: "flex",
              barPercentage: 1,
              categoryPercentage: 1,
              ticks: {
                maxRotation: 0,
                autoSkip: true,
                autoSkipPadding: 20,
              },
            },
          ],
        },
        zoom: {
          enabled: true,
          mode: "x",
        },
        pan: {
          enabled: true,
          mode: "x",
        },
      },
    });
    this.chart = chart;
  }

  canvasClick = () => {
    if (!this.tooltipTimeISO) return;
    const time = DateTime.fromISO(this.tooltipTimeISO);
    if (time.isValid) this.props.updateTime?.(time);
  };
}

export default observer(TrendChart);

export const chartColors = [
  "rgb(00, 172, 218)" /* light blue */,
  "rgb(250, 138, 24)" /* orange */,
  "rgb(30, 215, 125)" /* green */,
  "rgb(255, 79, 135)" /* pink */,
  // 'rgb(200, 180, 50)' /* yellow */
];

export const stColors = [
  "#e6194b",
  "#3cb44b",
  "#ffe119",
  "#4363d8",
  "#f58231",
  "#911eb4",
  "#46f0f0",
  "#f032e6",
  "#bcf60c",
  "#fabebe",
  "#008080",
  "#e6beff",
  "#9a6324",
  "#fffac8",
  "#800000",
  "#aaffc3",
  "#808000",
  "#ffd8b1",
  "#000075",
  "#808080",
  "#ffffff",
  "#000000",
];
